/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid } from '@mairistem/composants';
import { NaissanceForm } from '@mairistem/adresses';
import { EtatCivil, TypeComponent } from '@mairistem/administres-utils';

import { TypeDateNaissance } from '@/enums';
import { formatPrenomSaisie, resolveSexe, sexeToLibelle } from '@/utils';
import { composerNaissance, decomposerNaissance } from '@/utils/dateNaissance';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

// EtatCivil ne teste que _.isNil sur les nationalités : une chaîne vide y
// serait traitée comme renseignée. On ne transmet donc que les valeurs
// réellement non vides, les autres sont omises (undefined).
const sanitizeNationalite = (
  nationalite?: string | null,
): string | undefined =>
  _.isNil(nationalite) || nationalite === '' ? undefined : nationalite;

// EtatCivil est rendu dans une `<Grid padded>` qui ajoute 1rem de marge
// tout autour : on retire l'espace vertical en trop sur sa ligne et on décale
// les champs suivants d'autant pour qu'ils restent alignés dessous.
const etatCivilRowStyle = { paddingTop: 0, paddingBottom: 0 };
const alignedOnEtatCivilStyle = { paddingLeft: '2rem', paddingRight: '2rem' };

interface InformationsPartenaireNaissanceModalProps {
  partenaire: any;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsPartenaireNaissanceModal = (
  props: InformationsPartenaireNaissanceModalProps,
) => {
  const { partenaire, updated, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  // EtatCivil attend un individu au format Individu (sexe, jour/mois/année de
  // naissance séparés) alors que le partenaire de la convention est plat
  // (sexeCode, naissanceDate ISO) : on adapte dans les deux sens.
  const individuEtatCivil = React.useMemo(() => {
    return {
      sexe: resolveSexe(partenaire?.sexe, partenaire?.sexeCode) ?? undefined,
      nomFamille: partenaire?.nomFamille,
      prenom: partenaire?.prenom,
      prenomsComplementaires: partenaire?.prenomsComplementaires,
      // Précision enregistrée (jour ou mois inconnus) : mode C, M ou A
      ...decomposerNaissance(
        partenaire?.naissanceDate,
        partenaire?.naissanceTypeDate,
      ),
      premiereNationalite: sanitizeNationalite(partenaire?.premiereNationalite),
      deuxiemeNationalite: sanitizeNationalite(partenaire?.deuxiemeNationalite),
      troisiemeNationalite: sanitizeNationalite(
        partenaire?.troisiemeNationalite,
      ),
    };
  }, [partenaire]);

  // DateNaissanceInput envoie jour/mois/année via 3 appels onChange distincts
  // pour une même sélection : on les accumule dans une ref pour recomposer la
  // date complète sans écraser les valeurs déjà saisies dans ce même batch.
  const naissanceDateRef = React.useRef<{
    jour: number | null;
    mois: number | null;
    annee: number | null;
    type: TypeDateNaissance;
  }>({ jour: null, mois: null, annee: null, type: TypeDateNaissance.COMPLET });

  React.useEffect(() => {
    const naissance = decomposerNaissance(
      partenaire?.naissanceDate,
      partenaire?.naissanceTypeDate,
    );
    naissanceDateRef.current = {
      jour: naissance.jourNaissance ?? null,
      mois: naissance.moisNaissance ?? null,
      annee: naissance.anneeNaissance ?? null,
      type: naissance.typeDateNaissance,
    };
  }, [partenaire?.naissanceDate, partenaire?.naissanceTypeDate]);

  const handleChangeDateNaissancePart = React.useCallback(
    (field: 'jour' | 'mois' | 'annee' | 'type', value: any) => {
      naissanceDateRef.current = {
        ...naissanceDateRef.current,
        [field]: value,
      };
      const { jour, mois, annee, type } = naissanceDateRef.current;
      // Date (partie inconnue au 1er) et sa précision, enregistrée par l'API
      onChange(composerNaissance(jour, mois, annee, type));
    },
    [onChange],
  );

  const handleChangeEtatCivil = React.useCallback(
    (_e: React.SyntheticEvent | null, data: { name: string; value: any }) => {
      switch (data?.name) {
        case 'sexe':
          onChange({
            sexe: data.value ?? null,
            sexeCode: sexeToLibelle(data.value),
          });
          break;
        case 'jourNaissance':
          handleChangeDateNaissancePart('jour', data.value);
          break;
        case 'moisNaissance':
          handleChangeDateNaissancePart('mois', data.value);
          break;
        case 'anneeNaissance':
          handleChangeDateNaissancePart('annee', data.value);
          break;
        case 'typeDateNaissance':
          handleChangeDateNaissancePart('type', data.value);
          break;
        default:
          onChange({ [data.name]: formatPrenomSaisie(data.name, data.value) });
      }
    },
    [onChange, handleChangeDateNaissancePart],
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        naissanceLibelleCommuneDelegue: libelle,
        naissanceCommuneDelegue: drapeau,
      });
    },
    [onChange],
  );

  const handleChangeAdresseNaissance = React.useCallback(
    (adresse: Record<string, unknown>) => {
      onChange({
        ...(!_.isNil(adresse.codePostal) && {
          naissanceCodePostal: adresse.codePostal,
        }),
        ...(!_.isNil(adresse.commune) && {
          naissanceLocalite: adresse.commune,
        }),
        ...(!_.isNil(adresse.pays) && { naissancePays: adresse.pays }),
        ...(!_.isNil(adresse.departement) && {
          naissanceDepartementLibelle: adresse.departement,
        }),
        ...(!_.isNil(adresse.codeDepartement) && {
          naissanceDepartementCode: adresse.codeDepartement,
        }),
      });
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="partenaire-naissance-modal"
      errors={errors}
      loading={loading}
      header="INFORMATIONS DE NAISSANCE"
      updated={updated}
      scrollingContent
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row style={etatCivilRowStyle}>
            <Grid.Column width={16}>
              <EtatCivil
                typeComponent={TypeComponent.PACS}
                individu={individuEtatCivil}
                onChange={handleChangeEtatCivil}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} style={alignedOnEtatCivilStyle}>
              <NaissanceForm
                noForm
                adresse={{
                  codePostal: partenaire?.naissanceCodePostal,
                  commune: partenaire?.naissanceLocalite,
                  pays: partenaire?.naissancePays,
                  departement: partenaire?.naissanceDepartementLibelle,
                  codeDepartement: partenaire?.naissanceDepartementCode,
                }}
                onAdresseChange={handleChangeAdresseNaissance}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} style={alignedOnEtatCivilStyle}>
              <CommuneDelegueField
                data-testid="partenaire-naissance-libelle-commune-delegue"
                libelle={partenaire?.naissanceLibelleCommuneDelegue}
                drapeau={partenaire?.naissanceCommuneDelegue}
                onChange={handleChangeCommuneDelegue}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsPartenaireNaissanceModal;
